
import api, { LIST, STOREFRONT_SECTIONS } from '../Api'
import {ICollectionResponse} from "../../models/ICollectionResponse";
import {
    ISectionToTemplateRequest,
    IStorefrontSection,
    IStorefrontSectionCopyRequest,
    IStorefrontSectionsDelta
} from "../../models/Storefront";
import {IActionResponse} from "../../dal/BaseDAL";
import {cloneDeep} from "lodash";
import {ILookupConditions} from "../../models/Extension";
import {getQueryStringFromConditions} from "../../logic/StringHelper";

const storefrontSectionApi = api.injectEndpoints({
    endpoints: (build) => ({
        getStorefrontSections: build.query<ICollectionResponse<IStorefrontSection>, string>({
            query: (arg) => `storefront/storefronts/${arg}/sections`,
            providesTags: (_result, _error, storefront) => [{ type: STOREFRONT_SECTIONS, id: storefront }, { type: STOREFRONT_SECTIONS, id: LIST }]
        }),
        getStorefrontSection: build.query<IStorefrontSection, string>({
            query: (arg) => `storefront/storefronts/sections/${arg}`
        }),
        // Tagged so the Home editor's batch save invalidates these pickers — without it, sections
        // created this session (e.g. Selected Products on a custom page) never appear in the product
        // edit modal's section dropdowns until a manual refresh.
        getStorefrontProductDetailSections: build.query<ICollectionResponse<IStorefrontSection>, void>({
            query: () => `storefront/storefronts/sections/productDetails`,
            providesTags: [{ type: STOREFRONT_SECTIONS, id: 'productDetails' }]
        }),
        getStorefrontDynamicProductSections: build.query<ICollectionResponse<IStorefrontSection>, void>({
            query: () => `storefront/storefronts/sections/dynamicProducts`,
            providesTags: [{ type: STOREFRONT_SECTIONS, id: 'dynamicProducts' }]
        }),
        saveStorefrontSection: build.mutation<IActionResponse, {section: IStorefrontSection, storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/sections`,
                method: 'PUT',
                body: arg.section
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {

                const result = await queryFulfilled;
                if (!result.data.success) {
                    return;
                }

                const singlePatch = dispatch(
                    storefrontSectionApi.util.updateQueryData('getStorefrontSection', arg.section.id ?? '', draft => {
                        if(arg.section.id && arg.section.id !== '') {
                            Object.assign(draft, arg.section);
                        }
                    })
                );
                
                const detailsPatch = dispatch(
                    storefrontSectionApi.util.updateQueryData('getStorefrontProductDetailSections', undefined, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id && a.id === result.data.id)
                        if (index !== -1) {
                            copy.items.splice(index, 1, arg.section)
                        } else {
                            arg.section.id = result.data.id
                            copy.items.splice((copy.items.length - 1), 0, arg.section)
                        }
                        copy.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        Object.assign(draft, copy);
                    })
                );

                const patchResult = dispatch(
                    storefrontSectionApi.util.updateQueryData('getStorefrontSections', arg.storefront, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id && a.id === arg.section.id)
                        if (index !== -1) {
                            copy.items.splice(index, 1, arg.section)
                        } else {
                            arg.section.id = result.data.id
                            copy.items.splice((copy.items.length -1 ), 0, arg.section)
                        }
                        copy.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        Object.assign(draft, copy);
                    })
                );

            }
        }),
        copyStorefrontSection: build.mutation<IStorefrontSection, {request: IStorefrontSectionCopyRequest}>({
            query: (arg) => ({
                url:  `storefront/storefronts/sections/copy`,
                method: 'POST',
                body: arg.request
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;
                    if(result.data && result.data.storefront && result.data.storefront !== 'template') {
                        dispatch(
                            storefrontSectionApi.util.updateQueryData('getStorefrontSections', result.data.storefront, draft => {
                                const copy = cloneDeep(draft)
                                copy.items = [...copy.items, result.data]
                                copy.total = copy.items.length + 1
                                Object.assign(draft, copy);
                            })
                        );
                    }
                }
                catch (exception){
                    console.log(exception)
                }
            }
        }),
        getStorefrontSectionTemplates: build.query<ICollectionResponse<IStorefrontSection>, { conditions?: ILookupConditions } >({
            query: (arg) => `storefront/storefronts/sections/templates` + getQueryStringFromConditions(arg.conditions),
            serializeQueryArgs: ({endpointName}) => {
                return endpointName
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            }
        }),
        createStorefrontSectionTemplate: build.mutation<IActionResponse, {request: ISectionToTemplateRequest}>({
            query: (arg) => ({
                url:  `storefront/storefronts/sections/createTemplate`,
                method: 'POST',
                body: arg.request
            }),
        }),
        saveStorefrontSections: build.mutation<IActionResponse, {sections: IStorefrontSection[], storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/sections/batch`,
                method: 'PUT',
                body: arg.sections
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_SECTIONS, id: arg.storefront },
                // The batch save creates/deletes sections wholesale — refresh the type-filtered
                // pickers (product edit modal, dynamic product listings) too.
                { type: STOREFRONT_SECTIONS, id: 'productDetails' },
                { type: STOREFRONT_SECTIONS, id: 'dynamicProducts' },
            ]
        }),

        patchStorefrontSections: build.mutation<IActionResponse, {delta: IStorefrontSectionsDelta, storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/sections`,
                method: 'PATCH',
                body: arg.delta
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_SECTIONS, id: arg.storefront },
                // The delta save can create/delete sections — refresh the type-filtered
                // pickers (product edit modal, dynamic product listings) too.
                { type: STOREFRONT_SECTIONS, id: 'productDetails' },
                { type: STOREFRONT_SECTIONS, id: 'dynamicProducts' },
            ]
        }),

        deleteStorefrontSection: build.mutation<IActionResponse, {storefront: string, id: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/sections/${arg.id}`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    storefrontSectionApi.util.updateQueryData('getStorefrontSections', arg.storefront, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id === arg.id)
                        if(index !== -1){
                            copy.items.splice(index, 1)
                            Object.assign(draft, copy);
                        }

                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),
        deleteStorefrontSectionTemplate: build.mutation<IActionResponse, {id: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/sections/templates/${arg.id}`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    storefrontSectionApi.util.updateQueryData('getStorefrontSectionTemplates', {}, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id === arg.id)
                        if(index !== -1){
                            copy.items.splice(index, 1)
                            Object.assign(draft, copy);
                        }

                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),

    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontSectionsQuery,
    useSaveStorefrontSectionMutation,
    useDeleteStorefrontSectionMutation,
    useSaveStorefrontSectionsMutation,
    usePatchStorefrontSectionsMutation,
    useGetStorefrontProductDetailSectionsQuery,
    useGetStorefrontDynamicProductSectionsQuery,
    useCreateStorefrontSectionTemplateMutation,
    useGetStorefrontSectionTemplatesQuery,
    useDeleteStorefrontSectionTemplateMutation,
    useCopyStorefrontSectionMutation,
    useGetStorefrontSectionQuery
    
} = storefrontSectionApi
